/**
 * Central keyboard-shortcut map for the editor (spec §4.7). Kept as pure data + a resolver so the
 * bindings can be unit-tested and documented in one place, independent of DOM event wiring.
 */

export type ShortcutAction =
  | 'place'
  | 'plant-mode'
  | 'prev-plant'
  | 'next-plant'
  | 'move'
  | 'rotate'
  | 'maturity'
  | 'paint-ground'
  | 'sculpt-ground'
  | 'wand'
  | 'tag'
  | 'delete'
  | 'cancel'
  | 'undo'
  | 'redo'
  | 'select-all'
  | 'duplicate'
  | 'copy'
  | 'paste'
  | 'link'
  | 'unlink'
  | 'group'
  | 'ungroup'
  | 'save'
  | 'save-as'
  | 'rename'
  | 'focus-selection'
  | 'view-perspective'
  | 'view-top'
  | 'view-front'
  | 'toggle-grid'
  | 'toggle-topo'
  | 'toggle-pointers'
  | 'toggle-history'
  | 'photo-mode';

/** A normalized keyboard event descriptor (decoupled from the DOM `KeyboardEvent`). */
export interface ShortcutEvent {
  /** The event key, matched case-insensitively (e.g. 'g', 'Delete', 'Escape'). */
  readonly key: string;
  /** Ctrl or Cmd held. */
  readonly ctrl: boolean;
  readonly shift: boolean;
}

export interface ShortcutBinding {
  readonly action: ShortcutAction;
  readonly key: string;
  readonly ctrl: boolean;
  readonly shift: boolean;
  readonly description: string;
}

/** Build a {@link ShortcutEvent} from a DOM keyboard event (Cmd counts as Ctrl). */
export function toShortcutEvent(e: KeyboardEvent): ShortcutEvent {
  return { key: e.key, ctrl: e.ctrlKey || e.metaKey, shift: e.shiftKey };
}

/** The complete, documented shortcut map. Order matters: the first match wins. */
export const DEFAULT_SHORTCUTS: readonly ShortcutBinding[] = [
  // Ctrl+Shift+S is listed first: matching is on the full modifier triple, but reading order should
  // say that the shifted one is the copy rather than leaving it to be inferred from the plain one.
  { action: 'save-as', key: 's', ctrl: true, shift: true, description: 'Save a copy' },
  { action: 'save', key: 's', ctrl: true, shift: false, description: 'Save project' },
  // F2, where renaming lives on every file manager the user has ever used.
  { action: 'rename', key: 'f2', ctrl: false, shift: false, description: 'Rename site' },
  { action: 'duplicate', key: 'd', ctrl: true, shift: false, description: 'Duplicate selection' },
  { action: 'copy', key: 'c', ctrl: true, shift: false, description: 'Copy selection' },
  { action: 'paste', key: 'v', ctrl: true, shift: false, description: 'Paste' },
  { action: 'select-all', key: 'a', ctrl: true, shift: false, description: 'Select all' },
  { action: 'redo', key: 'z', ctrl: true, shift: true, description: 'Redo' },
  { action: 'undo', key: 'z', ctrl: true, shift: false, description: 'Undo' },
  { action: 'redo', key: 'y', ctrl: true, shift: false, description: 'Redo' },
  // Ctrl+Shift+H, beside undo and redo in the same modifier family — the panel is the long form of
  // those two, and H is free. Bare 'h' is deliberately left alone: it is the obvious key for a
  // future "hide", and a bare key that opens a panel is easy to hit by accident while planting.
  { action: 'toggle-history', key: 'h', ctrl: true, shift: true, description: 'Operations history' },
  { action: 'unlink', key: 'l', ctrl: true, shift: true, description: 'Unlink alias' },
  { action: 'link', key: 'l', ctrl: true, shift: false, description: 'Link alias group' },
  { action: 'ungroup', key: 'g', ctrl: true, shift: true, description: 'Ungroup' },
  { action: 'group', key: 'g', ctrl: true, shift: false, description: 'Group selection' },
  { action: 'place', key: 'p', ctrl: false, shift: false, description: 'Toggle placement' },
  // Shift+P, beside bare P for the tool itself. The everyday way to change planting mode is tapping
  // Ctrl with the tool in hand (see `PlacementTool`) — this is the named, discoverable form of the
  // same thing, for anyone who reads a shortcut list rather than a coachmark, and it arms the tool
  // on the way in so it means something from any starting state.
  { action: 'plant-mode', key: 'p', ctrl: false, shift: true, description: 'Place one / paint a drift' },
  { action: 'prev-plant', key: '[', ctrl: false, shift: false, description: 'Previous plant' },
  { action: 'next-plant', key: ']', ctrl: false, shift: false, description: 'Next plant' },
  { action: 'move', key: 'g', ctrl: false, shift: false, description: 'Move (grab)' },
  { action: 'rotate', key: 'r', ctrl: false, shift: false, description: 'Rotate' },
  { action: 'maturity', key: 'm', ctrl: false, shift: false, description: 'Adjust maturity' },
  // B for brush, T for terrain. Both tools then own `[ ] , . - =` for themselves while they are
  // live, which is why those bindings are not listed here — they never reach this map.
  { action: 'paint-ground', key: 'b', ctrl: false, shift: false, description: 'Paint ground' },
  { action: 'sculpt-ground', key: 't', ctrl: false, shift: false, description: 'Sculpt terrain' },
  // W for wand. Bare, like the other tool keys, and free — the editor claims no other 'w'.
  { action: 'wand', key: 'w', ctrl: false, shift: false, description: 'Magic wand' },
  // Shift+T for tag. T alone is already the terrain sculpt tool, and matching is on the full
  // modifier triple, so the two cannot shadow one another whatever order this list is in — the same
  // arrangement bare G, Shift+G and Ctrl+G already use.
  { action: 'tag', key: 't', ctrl: false, shift: true, description: 'Tag meaning' },
  { action: 'focus-selection', key: 'f', ctrl: false, shift: false, description: 'Focus selection' },
  // Bare 'c' — Ctrl+C is matched earlier in this list, so copy still wins when Ctrl is held.
  { action: 'photo-mode', key: 'c', ctrl: false, shift: false, description: 'Toggle photo mode' },
  { action: 'view-perspective', key: '1', ctrl: false, shift: false, description: 'Perspective view' },
  { action: 'view-top', key: '2', ctrl: false, shift: false, description: 'Top view' },
  { action: 'view-front', key: '3', ctrl: false, shift: false, description: 'Front view' },
  // Shift+G, next to bare G for move and Ctrl+Shift+G for ungroup. All three are matched on the
  // full modifier triple, so they cannot shadow one another whatever order this list is in.
  { action: 'toggle-grid', key: 'g', ctrl: false, shift: true, description: 'Toggle reference grid' },
  // Shift+C for contours, beside Shift+G for the grid — the two view aids get the two shifted keys.
  // Bare 'c' is photo mode and Ctrl+C is copy; all three are matched on the full modifier triple, so
  // none of them can shadow another whatever order this list is in.
  { action: 'toggle-topo', key: 'c', ctrl: false, shift: true, description: 'Toggle topographic map' },
  // Shift+N for the name plates, joining the shifted keys the other two view aids hold. Bare 'n' is
  // left free: a key that writes labels over the whole garden is not one to hit while reaching for M.
  { action: 'toggle-pointers', key: 'n', ctrl: false, shift: true, description: 'Toggle scene pointers' },
  { action: 'delete', key: 'delete', ctrl: false, shift: false, description: 'Delete selection' },
  { action: 'delete', key: 'backspace', ctrl: false, shift: false, description: 'Delete selection' },
  { action: 'cancel', key: 'escape', ctrl: false, shift: false, description: 'Cancel / deselect' },
];

/** Resolve a keyboard event to a shortcut action, or null when nothing matches. */
export function resolveShortcut(
  event: ShortcutEvent,
  bindings: readonly ShortcutBinding[] = DEFAULT_SHORTCUTS,
): ShortcutAction | null {
  const key = event.key.toLowerCase();
  for (const binding of bindings) {
    if (binding.key === key && binding.ctrl === event.ctrl && binding.shift === event.shift) {
      return binding.action;
    }
  }
  return null;
}
